<script setup>
import { computed, onBeforeUnmount, shallowRef, watch } from 'vue';
import UiCard from '@/components/ui/UiCard.vue';
import UiEmptyState from '@/components/ui/UiEmptyState.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useToast } from '@/composables/useToast';

useSessionGuard();

// The signature image printed on documents the user signs (e.g. a
// parent's acknowledgement). One per account.
const current = shallowRef(null); // { hasEsign, esign: { esign_filename } }
const loading = shallowRef(true);

async function load() {
  loading.value = true;
  try {
    const rows = await apiRequest('get', '/e-sigs/getMyEsign');
    current.value = Array.isArray(rows) ? rows[0] ?? null : rows;
  } catch {
    current.value = null;
  } finally {
    loading.value = false;
  }
}
load();

const savedUrl = computed(() =>
  current.value?.esign?.esign_filename
    ? `${process.env.VUE_APP_SERVER}/e-sigs/getEsign/${current.value.esign.esign_filename}`
    : '',
);

const file = shallowRef(null);
const picked = computed(() => (Array.isArray(file.value) ? file.value[0] : file.value) || null);
const previewUrl = shallowRef('');
watch(picked, (f) => {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = f ? URL.createObjectURL(f) : '';
});
onBeforeUnmount(() => previewUrl.value && URL.revokeObjectURL(previewUrl.value));

const shownUrl = computed(() => previewUrl.value || savedUrl.value);
const { busy, run } = useAsyncAction();
const toast = useToast();
const { confirm } = useConfirm();

async function upload() {
  if (!picked.value) return;
  if (picked.value.size > 4 * 1024 * 1024) {
    file.value = null;
    toast.error('The file is larger than 4 MB.');
    return;
  }
  const fd = new FormData();
  fd.append('file', picked.value);
  const ok = await run(() => apiRequest('post', '/e-sigs/uploadEsign', fd), {
    success: 'Signature saved.',
    error: 'Could not upload the signature.',
  });
  if (ok) {
    file.value = null;
    load();
  }
}

async function remove() {
  const ok = await confirm({
    title: 'Remove your signature?',
    description: "It won't be printed on documents you sign until you upload a new one.",
    confirmText: 'Remove',
    destructive: true,
  });
  if (!ok) return;
  const removed = await run(() => apiRequest('delete', '/e-sigs/my'), {
    success: 'Signature removed.',
    error: 'Could not remove the signature.',
  });
  if (removed) load();
}
</script>

<template>
  <UiCard title="E-signature" description="Printed on documents you sign. Use a PNG with a transparent background.">
    <div class="esig">
      <div class="esig__preview">
        <v-progress-circular v-if="loading" indeterminate size="24" width="2" />
        <img v-else-if="shownUrl" :src="shownUrl" alt="Your e-signature" />
        <UiEmptyState v-else icon="mdi-draw-pen" title="No signature yet" description="Upload one below." class="esig__empty" />
      </div>

      <form class="esig__form" @submit.prevent="upload">
        <UiFormField label="Signature image" description="PNG only, up to 4 MB." v-slot="{ id }">
          <v-file-input
            :id="id"
            v-model="file"
            accept="image/png"
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            :placeholder="savedUrl ? 'Replace your signature' : 'Choose a PNG file'"
            hide-details
          />
        </UiFormField>
        <div class="esig__actions">
          <v-btn type="submit" color="primary" :disabled="!picked" :loading="busy">
            {{ savedUrl ? 'Save new signature' : 'Upload signature' }}
          </v-btn>
          <v-btn v-if="savedUrl" variant="outlined" color="error" :disabled="busy" @click="remove">
            Remove signature
          </v-btn>
        </div>
      </form>
    </div>
  </UiCard>
</template>

<style scoped>
.esig {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 560px;
}
.esig__preview {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background-color: var(--subtle);
}
.esig__preview img {
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
}
.esig__empty {
  border: 0;
  padding: 0;
}
.esig__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.esig__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
